import React from "react";

import { ConfigSort } from "../types";
import { useAnalytics, useReactifySearchContext } from "../hooks";
import { debug } from "../utility";
import { getSortOption, getSortOptions } from "../utility/sortOption";

export const useSortBy = (): {
  /** The currently selected sort option */
  sortOption?: ConfigSort;
  /** All of the available sort options */
  sortOptions: Array<ConfigSort>;
  /** Function for changing the current sort option */
  setSortOption: (
    sortOptionHandle: string,
    ignoreHistoryState?: boolean
  ) => void;
} => {
  const reactifySearchContext = useReactifySearchContext();
  const { track } = useAnalytics();

  const sortOptions = React.useMemo(() => {
    return getSortOptions({
      provider: reactifySearchContext.options,
      config: reactifySearchContext.config,
    });
  }, [reactifySearchContext.options, reactifySearchContext.config]);

  const sortOption = React.useMemo(() => {
    return getSortOption({
      provider: reactifySearchContext.options,
      config: reactifySearchContext.config,
      sortOption: reactifySearchContext.sortby.sortOption,
    })
  }, [reactifySearchContext.options, reactifySearchContext.config, reactifySearchContext.sortby.sortOption]);

  const setSortOption = React.useCallback(
    (sortOptionHandle: string, ignoreHistoryState = false) => {
      if (typeof window === "undefined") {
        return;
      }

      debug.log(
        "useSortBy",
        "setSortOption[sortOptionHandle]",
        sortOptionHandle
      );

      reactifySearchContext.sortby.setSortOption(sortOptionHandle);

      if (!ignoreHistoryState) {
        const url = new URL(window.location.href);
        if (url.searchParams.has("sort")) {
          url.searchParams.set("sort", sortOptionHandle);
        } else {
          url.searchParams.append("sort", sortOptionHandle);
        }
        window.history.pushState({}, "", url.toString());
      }

      track({
        eventName: "sortChange",
        payload: {
          type: sortOptionHandle,
        },
      });
    },
    [typeof window === "undefined" ? "" : window.location.href]
  );

  debug.hook("useSortBy", "sortOption", sortOption);
  debug.hook("useSortBy", "sortOptions", sortOptions);

  return {
    sortOptions,
    sortOption,
    setSortOption,
  };
};
